import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.less']
})
export class VideoPlayerComponent implements OnInit, OnDestroy {
  @ViewChild('videoPlayer') videoPlayerRef?: ElementRef<HTMLVideoElement>;
  @ViewChild('videoSource') videoSourceRef?: ElementRef<HTMLSourceElement>;

  showVideo: boolean = false;
  attachmentId: string | null = null;
  isLoading: boolean = true;
  hasError: boolean = false;
  // 默认 16:9 作为加载中占位，视频 metadata 加载完成后会更新为真实比例
  videoAspectRatio: string = '16 / 9';
  private blobUrl: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      console.log('Route params:', params);
      if (params['attachmentId']) {
        this.attachmentId = params['attachmentId'];
        this.loadVideo();
      } else {
        this.extractAttachmentIdFromUrl();
      }
    });

    setTimeout(() => {
      if (!this.attachmentId) {
        this.extractAttachmentIdFromUrl();
      }
    }, 100);
  }

  extractAttachmentIdFromUrl(): void {
    const urlPath = window.location.pathname;
    const pathParts = urlPath.split('/').filter(part => part.length > 0);

    if (pathParts.length > 0) {
      const lastPart = pathParts[pathParts.length - 1];
      if (/^\d+$/.test(lastPart)) {
        this.attachmentId = lastPart;
        this.loadVideo();
        return;
      }
    }

    this.hasError = true;
    this.isLoading = false;
  }

  loadVideo(): void {
    if (!this.attachmentId) {
      this.hasError = true;
      this.isLoading = false;
      return;
    }

    const apiUrl = `/file/download/video/${this.attachmentId}`;

    console.log('Loading video with URL:', apiUrl);
    console.log('Attachment ID:', this.attachmentId);

    this.isLoading = true;
    this.hasError = false;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'isUserCenterAdmin': 'true'
    });

    this.http.get(apiUrl, {
      headers: headers,
      responseType: 'blob'
    }).subscribe({
      next: (blob: Blob) => {
        console.log('Video blob received, size:', blob.size, 'type:', blob.type);
        const videoBlob = new Blob([blob], { type: 'video/mp4' });
        this.blobUrl = URL.createObjectURL(videoBlob);
        this.showVideo = true;
        this.isLoading = false;
        setTimeout(() => this.loadAndPlayVideo(), 0);
      },
      error: (error) => {
        console.error('Error loading video:', error);
        this.hasError = true;
        this.isLoading = false;
        this.onVideoError();
      }
    });
  }

  private loadAndPlayVideo(): void {
    const videoEl = this.videoPlayerRef?.nativeElement;
    const sourceEl = this.videoSourceRef?.nativeElement;
    if (!videoEl || !sourceEl || !this.blobUrl) return;
    try {
      // 直接通过原生 DOM 设置 source.src，绕过 Angular 的 SafeUrl 绑定。
      // 部分国产浏览器（百度、360、QQ 等）基于较旧的 Chromium 内核，
      // 对 Angular 包装的 SafeValue 对象处理不稳定，会导致播放失败；
      // setAttribute 提供干净的 string URL，所有内核都能正确解析。
      sourceEl.setAttribute('src', this.blobUrl);
      videoEl.load();
      const playPromise = videoEl.play();
      if (playPromise !== undefined) {
        playPromise.catch((err) => {
          console.warn('视频自动播放被阻止，请手动点击播放:', err);
        });
      }
    } catch (err) {
      console.warn('视频播放异常:', err);
    }
  }

  onVideoError(): void {
    this.hasError = true;
    this.isLoading = false;
  }

  onVideoLoaded(): void {
    this.isLoading = false;
    this.hasError = false;
  }

  // metadata 加载完成后即可获取视频真实宽高，比 loadeddata 更早触发
  onVideoMetadataLoaded(): void {
    const videoEl = this.videoPlayerRef?.nativeElement;
    if (videoEl && videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
      this.videoAspectRatio = `${videoEl.videoWidth} / ${videoEl.videoHeight}`;
    }
  }

  ngOnDestroy(): void {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
  }
}

